import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useRpc, useListVersion, useSyncStatus } from "../store";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { useColors, useStyles, getTriageColor, badge } from "../ui";
import { useAnon } from "../anon";
import { db } from "../db";
import * as s from "../../../shared/src/schema";
import { eq } from "drizzle-orm";
import type { InsightsResult } from "../../../shared/src/protocol";

interface Hospital {
  id: string;
  name: string | null;
  regionId: string | null;
}
interface Unit {
  id: string;
  name: string | null;
}
interface Region {
  id: string;
  name: string | null;
}

type ScopeMode = "all" | "hospitals" | "units";
type TimeRange = "all" | "30d" | "90d" | "1y" | "custom";
type Granularity = "day" | "week" | "month";

// Module-level cache — survives unmount so navigating back is instant.
let _cache: InsightsResult | null = null;

const SCOPE_MODES: ScopeMode[] = ["all", "hospitals", "units"];
const TIME_RANGES: TimeRange[] = ["all", "30d", "90d", "1y", "custom"];
const GRANULARITIES: Granularity[] = ["day", "week", "month"];

export function InsightsPage() {
  const rpc = useRpc();
  const {} = useAuth();
  const { t } = useI18n();
  const colors = useColors();
  const styles = useStyles();
  const { anonUnit, anonHospital, anonRegion } = useAnon();
  const listVersion = useListVersion();
  const { online, syncing } = useSyncStatus();
  const [searchParams, setSearchParams] = useSearchParams();

  // Reference data
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);

  // Read state from URL params
  const scopeMode = (
    SCOPE_MODES.includes(searchParams.get("scope") as ScopeMode)
      ? searchParams.get("scope")
      : "all"
  ) as ScopeMode;
  const timeRange = (
    TIME_RANGES.includes(searchParams.get("time") as TimeRange)
      ? searchParams.get("time")
      : "30d"
  ) as TimeRange;
  const granularity = (
    GRANULARITIES.includes(searchParams.get("gran") as Granularity)
      ? searchParams.get("gran")
      : "day"
  ) as Granularity;
  const customFrom = searchParams.get("from") || "";
  const customTo = searchParams.get("to") || "";
  const selectedHospitalIds =
    searchParams.get("hospitals")?.split(",").filter(Boolean) || [];
  const selectedUnitIds =
    searchParams.get("units")?.split(",").filter(Boolean) || [];

  // Helper to update URL params without replacing the whole object
  const setParam = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (
              v === undefined ||
              v === "" ||
              (v === "all" && k === "scope") ||
              (v === "30d" && k === "time") ||
              (v === "day" && k === "gran")
            ) {
              next.delete(k);
            } else {
              next.set(k, v);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Data — initialise from module cache to avoid flash on re-mount
  const [data, setData] = useState<InsightsResult | null>(_cache);
  const [loading, setLoading] = useState(!_cache);

  // Load reference data from local DB
  useEffect(() => {
    Promise.all([
      db.select().from(s.hospitals).where(eq(s.hospitals.archived, 0)),
      db.select().from(s.units).where(eq(s.units.archived, 0)),
      db.select().from(s.regions).where(eq(s.regions.archived, 0)),
    ]).then(([h, u, r]) => {
      setHospitals(h as Hospital[]);
      setUnits(u as Unit[]);
      setRegions(r as Region[]);
    });
  }, [listVersion]);

  // Compute start/end timestamps from time range
  const getTimeParams = useCallback((): {
    startTime?: number;
    endTime?: number;
  } => {
    const now = Date.now();
    switch (timeRange) {
      case "30d":
        return { startTime: now - 30 * 86400000 };
      case "90d":
        return { startTime: now - 90 * 86400000 };
      case "1y":
        return { startTime: now - 365 * 86400000 };
      case "custom": {
        const start = customFrom ? new Date(customFrom).getTime() : undefined;
        const end = customTo
          ? new Date(customTo + "T23:59:59").getTime()
          : undefined;
        return { startTime: start, endTime: end };
      }
      default:
        return {};
    }
  }, [timeRange, customFrom, customTo]);

  // Fetch insights via RPC
  const fetchInsights = useCallback(async () => {
    if (!_cache) setLoading(true);
    try {
      const opts: {
        hospitalIds?: string[];
        unitIds?: string[];
        startTime?: number;
        endTime?: number;
        granularity: Granularity;
      } = {
        ...getTimeParams(),
        granularity,
      };
      if (scopeMode === "hospitals" && selectedHospitalIds.length > 0) {
        opts.hospitalIds = selectedHospitalIds;
      }
      if (scopeMode === "units" && selectedUnitIds.length > 0) {
        opts.unitIds = selectedUnitIds;
      }
      const result = await rpc<InsightsResult>({ type: "insights", ...opts });
      setData(result);
      _cache = result;
    } catch (e) {
      console.error("[Insights] fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [
    rpc,
    scopeMode,
    selectedHospitalIds.join(","),
    selectedUnitIds.join(","),
    getTimeParams,
    granularity,
  ]);

  // Fetch when connected (online + sync done) or when server invalidates data
  const ready = online && !syncing;
  useEffect(() => {
    if (ready) fetchInsights();
  }, [fetchInsights, ready, listVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group hospitals by region for display
  const regionMap = new Map(
    regions.map((r) => [r.id, r.name || r.id.slice(-6)]),
  );
  const hospitalsByRegion = new Map<string, Hospital[]>();
  for (const h of hospitals) {
    const key = h.regionId || "__none__";
    const list = hospitalsByRegion.get(key) || [];
    list.push(h);
    hospitalsByRegion.set(key, list);
  }

  const toggleHospital = (id: string) => {
    const next = selectedHospitalIds.includes(id)
      ? selectedHospitalIds.filter((x) => x !== id)
      : [...selectedHospitalIds, id];
    setParam({ hospitals: next.length ? next.join(",") : undefined });
  };

  const toggleUnit = (id: string) => {
    const next = selectedUnitIds.includes(id)
      ? selectedUnitIds.filter((x) => x !== id)
      : [...selectedUnitIds, id];
    setParam({ units: next.length ? next.join(",") : undefined });
  };

  const selectRegionHospitals = (regionId: string) => {
    const regionHospitals = (hospitalsByRegion.get(regionId) || []).map(
      (h) => h.id,
    );
    const allSelected = regionHospitals.every((id) =>
      selectedHospitalIds.includes(id),
    );
    const next = allSelected
      ? selectedHospitalIds.filter((id) => !regionHospitals.includes(id))
      : [...new Set([...selectedHospitalIds, ...regionHospitals])];
    setParam({ hospitals: next.length ? next.join(",") : undefined });
  };

  const triageColors = getTriageColor(colors);
  const timeParams = getTimeParams();
  const filledBuckets = data
    ? fillBuckets(
        data.byMonth,
        granularity,
        timeParams.startTime,
        timeParams.endTime,
      )
    : [];
  const maxCases = Math.max(...filledBuckets.map((m) => m.cases), 1);
  const maxDeaths = Math.max(...filledBuckets.map((m) => m.deaths), 1);
  const chartTotal = maxCases + maxDeaths;
  const casesH = Math.max(2, Math.round((maxCases / chartTotal) * 14));
  const deathsH = Math.max(1, 14 - casesH);
  const labelStep = Math.max(1, Math.ceil(filledBuckets.length / 40));
  const [activeBar, setActiveBar] = useState<number | null>(null);
  const controlLabel: React.CSSProperties = {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: "uppercase",
  };

  return (
    <div style={{ overflow: "auto", height: "100%", padding: "0.75rem" }}>
      {/* Controls row */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          marginBottom: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        {/* Time range */}
        <div style={{ display: "flex", gap: "0.375rem", alignItems: "center" }}>
          <span style={controlLabel}>{t("insights.timeRange")}:</span>
          <select
            value={timeRange}
            onChange={(e) => {
              const next = e.target.value as TimeRange;
              const autoGran =
                next === "30d" || next === "90d"
                  ? "day"
                  : next === "1y"
                    ? "week"
                    : next === "all"
                      ? "month"
                      : undefined;
              if (next === "custom" && timeRange !== "custom") {
                const today = new Date().toISOString().slice(0, 10);
                const daysAgo = (d: number) =>
                  new Date(Date.now() - d * 86400000)
                    .toISOString()
                    .slice(0, 10);
                const from =
                  timeRange === "30d"
                    ? daysAgo(30)
                    : timeRange === "90d"
                      ? daysAgo(90)
                      : timeRange === "1y"
                        ? daysAgo(365)
                        : "";
                setParam({ time: next, from: from || undefined, to: today });
              } else {
                setParam({
                  time: next,
                  from: undefined,
                  to: undefined,
                  ...(autoGran ? { gran: autoGran } : {}),
                });
              }
            }}
            style={{ ...styles.selectCompact, width: "auto" }}
          >
            {TIME_RANGES.map((range) => (
              <option key={range} value={range}>
                {t(`insights.timeRange_${range}`)}
              </option>
            ))}
          </select>
          {timeRange === "custom" && (
            <>
              <input
                type="date"
                value={customFrom}
                onChange={(e) =>
                  setParam({ from: e.target.value || undefined })
                }
                style={{ ...styles.inputCompact, width: "9rem" }}
              />
              <span style={{ fontSize: "0.8rem", color: colors.textMuted }}>
                &ndash;
              </span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setParam({ to: e.target.value || undefined })}
                style={{ ...styles.inputCompact, width: "9rem" }}
              />
            </>
          )}
        </div>

        {/* Granularity */}
        <div style={{ display: "flex", gap: "0.375rem", alignItems: "center" }}>
          <span style={controlLabel}>{t("insights.granularity")}:</span>
          <select
            value={granularity}
            onChange={(e) => setParam({ gran: e.target.value })}
            style={{ ...styles.selectCompact, width: "auto" }}
          >
            {GRANULARITIES.map((g) => (
              <option key={g} value={g}>
                {t(`insights.granularity_${g}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Scope selector */}
        <div style={{ display: "flex", gap: "0.375rem", alignItems: "center" }}>
          <span style={controlLabel}>{t("insights.scope")}:</span>
          {SCOPE_MODES.map((mode) => (
            <button
              key={mode}
              onClick={() =>
                setParam({
                  scope: mode,
                  hospitals: undefined,
                  units: undefined,
                })
              }
              style={{
                ...styles.btnSmall,
                background: scopeMode === mode ? colors.primary : colors.bg,
                color: scopeMode === mode ? "#fff" : colors.text,
                borderColor:
                  scopeMode === mode ? colors.primary : colors.border,
              }}
            >
              {t(`insights.scope_${mode}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Hospital/Unit selector */}
      {scopeMode === "hospitals" && (
        <div
          style={{
            marginBottom: "0.75rem",
            border: `1px solid ${colors.border}`,
            padding: "0.5rem",
            maxHeight: "12rem",
            overflow: "auto",
          }}
        >
          {[...hospitalsByRegion.entries()].map(
            ([regionId, regionHospitals]) => {
              const regionName =
                regionId === "__none__"
                  ? t("insights.noRegion")
                  : anonRegion(regionId, regionMap.get(regionId) || regionId.slice(-6));
              const allSelected = regionHospitals.every((h) =>
                selectedHospitalIds.includes(h.id),
              );
              return (
                <div key={regionId} style={{ marginBottom: "0.375rem" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.375rem",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: colors.textMuted,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => selectRegionHospitals(regionId)}
                    />
                    {regionName}
                  </label>
                  <div
                    style={{
                      paddingLeft: "1.25rem",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.25rem 0.75rem",
                    }}
                  >
                    {regionHospitals.map((h) => (
                      <label
                        key={h.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          fontSize: "0.8rem",
                          color: colors.text,
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedHospitalIds.includes(h.id)}
                          onChange={() => toggleHospital(h.id)}
                        />
                        {anonHospital(h.id, h.name || h.id.slice(-6))}
                      </label>
                    ))}
                  </div>
                </div>
              );
            },
          )}
        </div>
      )}

      {scopeMode === "units" && (
        <div
          style={{
            marginBottom: "0.75rem",
            border: `1px solid ${colors.border}`,
            padding: "0.5rem",
            maxHeight: "12rem",
            overflow: "auto",
            display: "flex",
            flexWrap: "wrap",
            gap: "0.25rem 0.75rem",
          }}
        >
          {units.map((u) => (
            <label
              key={u.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                fontSize: "0.8rem",
                color: colors.text,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={selectedUnitIds.includes(u.id)}
                onChange={() => toggleUnit(u.id)}
              />
              {anonUnit(u.id, u.name || u.id.slice(-6))}
            </label>
          ))}
        </div>
      )}

      {loading && !data && (
        <div
          style={{
            padding: "1.5rem",
            color: colors.textMuted,
            textAlign: "center",
          }}
        >
          {t("app.loading")}
        </div>
      )}

      {data && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(18rem, 1fr))",
            gap: "0.75rem",
          }}
        >
          {/* Summary row */}
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              alignItems: "stretch",
              gridColumn: "1 / -1",
              flexWrap: "wrap",
            }}
          >
            <SummaryCard
              label={t("insights.totalCases")}
              value={data.totalCases}
              color={colors.primary}
            />
            <SummaryCard
              label={t("insights.totalDeaths")}
              value={data.totalDeaths}
              color={colors.danger}
            />
            {data.byTriage.length > 0 && (
              <div
                style={{
                  border: `1px solid ${colors.border}`,
                  padding: "0.5rem 0.75rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                {data.byTriage
                  .sort((a, b) => a.category.localeCompare(b.category))
                  .map((row) => (
                    <div
                      key={row.category}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                      }}
                    >
                      <span
                        style={badge(
                          triageColors[row.category] || colors.textMuted,
                        )}
                      >
                        {row.category}
                      </span>
                      <span style={{ fontWeight: 700, color: colors.text }}>
                        {row.count}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Timeline chart */}
          <Card title={t("insights.byMonth")} colors={colors} wide>
            {filledBuckets.length === 0 ? (
              <EmptyMsg colors={colors} />
            ) : (
              <div
                style={{ display: "flex", gap: "1px" }}
                onMouseLeave={() => setActiveBar(null)}
              >
                {filledBuckets.map((row, i) => {
                  const casePct =
                    row.cases > 0
                      ? Math.max(4, (row.cases / maxCases) * 100)
                      : 0;
                  const deathPct =
                    row.deaths > 0
                      ? Math.max(4, (row.deaths / maxDeaths) * 100)
                      : 0;
                  const showLabel =
                    i % labelStep === 0 || i === filledBuckets.length - 1;
                  const isActive = activeBar === i;
                  return (
                    <div
                      key={row.period}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minWidth: 0,
                        position: "relative",
                      }}
                      onMouseEnter={() => setActiveBar(i)}
                      onClick={() =>
                        setActiveBar((prev) => (prev === i ? null : i))
                      }
                    >
                      {isActive && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "100%",
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: colors.text,
                            color: colors.bg,
                            padding: "0.25rem 0.5rem",
                            fontSize: "0.65rem",
                            whiteSpace: "nowrap",
                            zIndex: 10,
                            pointerEvents: "none",
                            fontFamily: "ui-monospace, monospace",
                          }}
                        >
                          {row.period} — {row.cases}
                          {row.deaths > 0 ? ` / ${row.deaths}\u2020` : ""}
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          height: `${casesH}rem`,
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: casePct > 0 ? `${casePct}%` : "1px",
                            background:
                              casePct > 0
                                ? isActive
                                  ? colors.text
                                  : colors.primary
                                : colors.border,
                            transition: "background 0.1s",
                          }}
                        />
                      </div>
                      {showLabel ? (
                        <span
                          style={{
                            fontSize: "0.55rem",
                            color: colors.textMuted,
                            whiteSpace: "nowrap",
                            writingMode: "vertical-rl",
                            lineHeight: 1,
                            fontFamily: "ui-monospace, monospace",
                          }}
                        >
                          {formatPeriodLabel(row.period)}
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "0.55rem",
                            writingMode: "vertical-rl",
                            lineHeight: 1,
                            visibility: "hidden",
                          }}
                        >
                          {"\u00A0"}
                        </span>
                      )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          height: `${deathsH}rem`,
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: `${deathPct}%`,
                            background: isActive ? colors.text : colors.danger,
                            transition: "background 0.1s",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {filledBuckets.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  marginTop: "0.375rem",
                  fontSize: "0.7rem",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "0.625rem",
                      height: "0.625rem",
                      background: colors.primary,
                    }}
                  />
                  {t("insights.cases")}
                </span>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "0.625rem",
                      height: "0.625rem",
                      background: colors.danger,
                    }}
                  />
                  {t("insights.deaths")}
                </span>
              </div>
            )}
          </Card>

          {/* By region — hidden when exactly 1 region's hospitals are selected */}
          {data.byRegion.length > 1 && (
            <Card title={t("insights.byRegion")} colors={colors}>
              <table style={{ ...styles.table, fontSize: "0.8rem" }}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, fontSize: "0.7rem" }}>
                      {t("admin.regions")}
                    </th>
                    <th
                      style={{
                        ...styles.th,
                        fontSize: "0.7rem",
                        textAlign: "right",
                        color: colors.primary,
                      }}
                    >
                      {t("insights.cases")}
                    </th>
                    <th
                      style={{
                        ...styles.th,
                        fontSize: "0.7rem",
                        textAlign: "right",
                        color: colors.danger,
                      }}
                    >
                      {t("insights.deaths")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.byRegion.map((row) => (
                    <tr key={row.id}>
                      <td style={styles.td}>{anonRegion(row.id, row.name || row.id.slice(-6))}</td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {row.cases}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: 600,
                          color:
                            row.deaths > 0 ? colors.danger : colors.textMuted,
                        }}
                      >
                        {row.deaths}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ ...styles.td, fontWeight: 700 }}>
                      {t("insights.total")}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "right",
                        fontWeight: 700,
                        color: colors.primary,
                      }}
                    >
                      {data.byRegion.reduce((s, r) => s + r.cases, 0)}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "right",
                        fontWeight: 700,
                        color: colors.danger,
                      }}
                    >
                      {data.byRegion.reduce((s, r) => s + r.deaths, 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </Card>
          )}

          {/* By hospital — hidden when exactly 1 hospital selected */}
          {!(scopeMode === "hospitals" && selectedHospitalIds.length === 1) && (
            <Card title={t("insights.byHospital")} colors={colors}>
              {data.byHospital.length === 0 ? (
                <EmptyMsg colors={colors} />
              ) : (
                <table style={{ ...styles.table, fontSize: "0.8rem" }}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, fontSize: "0.7rem" }}>
                        {t("admin.hospitals")}
                      </th>
                      <th
                        style={{
                          ...styles.th,
                          fontSize: "0.7rem",
                          textAlign: "right",
                          color: colors.primary,
                        }}
                      >
                        {t("insights.cases")}
                      </th>
                      <th
                        style={{
                          ...styles.th,
                          fontSize: "0.7rem",
                          textAlign: "right",
                          color: colors.danger,
                        }}
                      >
                        {t("insights.deaths")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byHospital.map((row) => (
                      <tr key={row.id}>
                        <td style={styles.td}>
                          {anonHospital(row.id, row.name || row.id.slice(-6))}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            textAlign: "right",
                            fontWeight: 600,
                          }}
                        >
                          {row.cases}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            textAlign: "right",
                            fontWeight: 600,
                            color:
                              row.deaths > 0 ? colors.danger : colors.textMuted,
                          }}
                        >
                          {row.deaths}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {t("insights.total")}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: 700,
                          color: colors.primary,
                        }}
                      >
                        {data.byHospital.reduce((s, r) => s + r.cases, 0)}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: 700,
                          color: colors.danger,
                        }}
                      >
                        {data.byHospital.reduce((s, r) => s + r.deaths, 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </Card>
          )}

          {/* By unit — hidden when exactly 1 unit selected */}
          {!(scopeMode === "units" && selectedUnitIds.length === 1) && (
            <Card title={t("insights.byUnit")} colors={colors}>
              {data.byUnit.length === 0 ? (
                <EmptyMsg colors={colors} />
              ) : (
                <table style={{ ...styles.table, fontSize: "0.8rem" }}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, fontSize: "0.7rem" }}>
                        {t("admin.units")}
                      </th>
                      <th
                        style={{
                          ...styles.th,
                          fontSize: "0.7rem",
                          textAlign: "right",
                          color: colors.primary,
                        }}
                      >
                        {t("insights.cases")}
                      </th>
                      <th
                        style={{
                          ...styles.th,
                          fontSize: "0.7rem",
                          textAlign: "right",
                          color: colors.danger,
                        }}
                      >
                        {t("insights.deaths")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byUnit.map((row) => (
                      <tr key={row.id}>
                        <td style={styles.td}>
                          {anonUnit(row.id, row.name || row.id.slice(-6))}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            textAlign: "right",
                            fontWeight: 600,
                          }}
                        >
                          {row.cases}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            textAlign: "right",
                            fontWeight: 600,
                            color:
                              row.deaths > 0 ? colors.danger : colors.textMuted,
                          }}
                        >
                          {row.deaths}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {t("insights.total")}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: 700,
                          color: colors.primary,
                        }}
                      >
                        {data.byUnit.reduce((s, r) => s + r.cases, 0)}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: 700,
                          color: colors.danger,
                        }}
                      >
                        {data.byUnit.reduce((s, r) => s + r.deaths, 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </Card>
          )}

          {/* By trauma type */}
          <Card title={t("insights.byTrauma")} colors={colors}>
            {data.byTrauma.length === 0 ? (
              <EmptyMsg colors={colors} />
            ) : (
              <table style={{ ...styles.table, fontSize: "0.8rem" }}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, fontSize: "0.7rem" }}></th>
                    <th
                      style={{
                        ...styles.th,
                        fontSize: "0.7rem",
                        textAlign: "right",
                        color: colors.primary,
                      }}
                    >
                      {t("insights.cases")}
                    </th>
                    <th
                      style={{
                        ...styles.th,
                        fontSize: "0.7rem",
                        textAlign: "right",
                        color: colors.danger,
                      }}
                    >
                      {t("insights.deaths")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.byTrauma.map((row) => (
                    <tr key={row.type}>
                      <td style={styles.td}>
                        {t(`trauma.${row.type}`) || row.type}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {row.cases}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: 600,
                          color:
                            row.deaths > 0 ? colors.danger : colors.textMuted,
                        }}
                      >
                        {row.deaths}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ ...styles.td, fontWeight: 700 }}>
                      {t("insights.total")}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "right",
                        fontWeight: 700,
                        color: colors.primary,
                      }}
                    >
                      {data.byTrauma.reduce((s, r) => s + r.cases, 0)}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "right",
                        fontWeight: 700,
                        color: colors.danger,
                      }}
                    >
                      {data.byTrauma.reduce((s, r) => s + r.deaths, 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── Helper components ───────────────────────────────────────────────

/** Shorten period label for display: 2025-03 → 03, 2025-03-15 → 03-15, 2025-W12 → W12 */
function formatPeriodLabel(period: string): string {
  if (period.includes("-W")) return period.slice(5); // W12
  const parts = period.split("-");
  return parts.length === 3
    ? `${parts[1]}-${parts[2]}`
    : parts.slice(1).join("-");
}

type Bucket = { period: string; cases: number; deaths: number };

/** Fill buckets across the full time range so the chart always shows the complete period.
 *  Uses UTC dates to match server-side strftime(..., 'unixepoch') which is UTC-based. */
function fillBuckets(
  sparse: Bucket[],
  gran: Granularity,
  startTime?: number,
  endTime?: number,
): Bucket[] {
  const lookup = new Map(sparse.map((b) => [b.period, b]));
  const now = new Date();
  const startDate = startTime
    ? new Date(startTime)
    : sparse.length > 0
      ? parsePeriodStart(sparse[0].period)
      : now;
  const endDate = endTime ? new Date(endTime) : now;
  const empty = { cases: 0, deaths: 0 };

  if (gran === "week") {
    const periods: string[] = [];
    const d = new Date(startDate);
    // Align to Monday (UTC). getUTCDay(): 0=Sun,1=Mon,...
    const dow = d.getUTCDay();
    d.setUTCDate(d.getUTCDate() - ((dow + 6) % 7)); // back to Monday
    while (d <= endDate) {
      const y = d.getUTCFullYear();
      const jan1 = new Date(Date.UTC(y, 0, 1));
      // SQLite %W: number of Mondays so far (week starts on Monday, 00-based)
      const dayOfYear = Math.floor((d.getTime() - jan1.getTime()) / 86400000);
      const jan1Day = jan1.getUTCDay(); // 0=Sun
      const w = Math.floor((dayOfYear + ((jan1Day + 6) % 7)) / 7);
      periods.push(`${y}-W${String(w).padStart(2, "0")}`);
      d.setUTCDate(d.getUTCDate() + 7);
    }
    return periods.map((p) => lookup.get(p) || { period: p, ...empty });
  }

  if (gran === "day") {
    const periods: string[] = [];
    const d = new Date(
      Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate(),
      ),
    );
    const end = new Date(
      Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate(),
      ),
    );
    while (d <= end) {
      periods.push(d.toISOString().slice(0, 10));
      d.setUTCDate(d.getUTCDate() + 1);
    }
    return periods.map((p) => lookup.get(p) || { period: p, ...empty });
  }

  // Monthly (UTC)
  const periods: string[] = [];
  let y = startDate.getUTCFullYear(),
    m = startDate.getUTCMonth() + 1;
  const ey = endDate.getUTCFullYear(),
    em = endDate.getUTCMonth() + 1;
  while (y < ey || (y === ey && m <= em)) {
    periods.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return periods.map((p) => lookup.get(p) || { period: p, ...empty });
}

function parsePeriodStart(period: string): Date {
  if (period.includes("-W")) {
    const [y, w] = period.split("-W").map(Number);
    const jan1 = new Date(y, 0, 1);
    const d = new Date(jan1.getTime() + (w - 1) * 7 * 86400000);
    return d;
  }
  const parts = period.split("-").map(Number);
  if (parts.length === 3) return new Date(parts[0], parts[1] - 1, parts[2]);
  return new Date(parts[0], parts[1] - 1, 1);
}

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  const colors = useColors();
  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        padding: "0.75rem",
        textAlign: "center",
      }}
    >
      <div
        style={{ fontSize: "2rem", fontWeight: 700, color, minWidth: "4ch" }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: "0.75rem",
          color: colors.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </div>
    </div>
  );
}

function Card({
  title,
  children,
  colors,
  wide,
}: {
  title: string;
  children: React.ReactNode;
  colors: ReturnType<typeof useColors>;
  wide?: boolean;
}) {
  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        gridColumn: wide ? "1 / -1" : undefined,
      }}
    >
      <div
        style={{
          padding: "0.5rem 0.75rem",
          fontWeight: 600,
          fontSize: "0.8rem",
          borderBottom: `1px solid ${colors.border}`,
          background: colors.bgAlt,
          color: colors.text,
        }}
      >
        {title}
      </div>
      <div style={{ padding: "0.75rem" }}>{children}</div>
    </div>
  );
}

function EmptyMsg({ colors }: { colors: ReturnType<typeof useColors> }) {
  return (
    <div
      style={{
        fontSize: "0.8rem",
        color: colors.textMuted,
        textAlign: "center",
        padding: "0.5rem",
      }}
    >
      —
    </div>
  );
}
